/**
 * Tenant SPA API client. Same oRPC `{json: ...}` wrap pattern as the
 * admin SPA — kept in two files so each can evolve independently.
 */

import { getToken } from './token-provider.js';
import { newClientTrace, rememberTrace } from './trace.js';
import type { ColumnMapping } from '@regta/common/data-table-mapping';
import type {
  ActionLog,
  ChangeGroup,
  NewActionLogEntry,
  PatchChange,
  ReplayReportEntry,
  ResolvedDocumentIR,
  CompileContextInput,
  SuggestionInfo,
} from '@hyranai/regta-document-ir';

const AUTH_BASE = import.meta.env['VITE_AUTH_BACKEND_URL'] as string;
const REGTA_BASE = import.meta.env['VITE_REGTA_BACKEND_URL'] as string;

export type MintResponse = { token: string; expires_at: number };

/**
 * Backend client-error envelope (ADR-0040). The auth-backend now also
 * emits this shape (per the auth-refresh fix); the older
 * `{ error, message }` shape is kept as a parse fallback.
 */
type ClientErrorBody = {
  error?: string;
  message?: string;
  trace_id?: string | null;
  /** oRPC error `data` (structured reason, diagnostics, …) when the procedure set one. */
  data?: unknown;
};

/** Thrown by `rpc()` / `mintRegtaJwt` so callers can inspect the code. */
export class ApiError extends Error {
  status: number;
  code: string;
  traceId: string | null;
  /**
   * The procedure's structured error payload (oRPC `data`), e.g.
   * `{ reason, diagnostics }` for `ir_document_has_errors`. Untyped: each
   * call site narrows what it expects.
   */
  data?: Record<string, unknown>;
  constructor(opts: {
    status: number;
    code: string;
    message: string;
    traceId: string | null;
    data?: unknown;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.traceId = opts.traceId;
    if (opts.data && typeof opts.data === 'object' && !Array.isArray(opts.data)) {
      this.data = opts.data as Record<string, unknown>;
    }
  }
}

function parseEnvelope(body: unknown): ClientErrorBody | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  // ADR-0040 envelope on Express / auth-backend.
  if (typeof b['error'] === 'string' || typeof b['trace_id'] === 'string') {
    return b as ClientErrorBody;
  }
  // oRPC procedure error: { json: { code, message, ... } }
  const j = b['json'];
  if (j && typeof j === 'object') {
    const jj = j as Record<string, unknown>;
    if (typeof jj['code'] === 'string') {
      return {
        error: jj['code'] as string,
        message: jj['message'] as string,
        ...(jj['data'] !== undefined ? { data: jj['data'] } : {}),
      };
    }
  }
  return null;
}

export async function mintRegtaJwt(clerkJwt: string): Promise<MintResponse> {
  const trace = newClientTrace();
  rememberTrace(trace);
  const res = await fetch(`${AUTH_BASE}/auth/mint`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', traceparent: trace.traceparent },
    credentials: 'include',
    body: JSON.stringify({ clerk_jwt: clerkJwt, audience: 'regta:backend' }),
  });
  const serverTrace =
    res.headers.get('x-trace-id') ?? res.headers.get('traceparent') ?? trace.traceId;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const env = parseEnvelope(body);
    throw new ApiError({
      status: res.status,
      code: env?.error ?? `HTTP_${res.status}`,
      message: env?.message ?? res.statusText,
      traceId: env?.trace_id ?? serverTrace,
    });
  }
  return body as MintResponse;
}

/**
 * Active-group selection, kept at module scope so `rpc()` can attach it
 * to every outbound request without each call site threading it through.
 *
 * `ActiveGroupProvider` calls `setActiveGroupHeader()` whenever the
 * user's selection changes. Module-mutable state is normally a smell,
 * but here it's the cleanest fit: the value is owned by a single React
 * context and consumed by a non-React HTTP helper.
 */
let _activeGroupHeader: string | null = null;

export function setActiveGroupHeader(groupId: string | null): void {
  _activeGroupHeader = groupId;
}

/**
 * Codes that indicate the Regta JWT itself is no longer valid. When we
 * see one of these on a 401 we force-refresh the token and retry once.
 * Anything else (e.g. FORBIDDEN, missing_token without an expiry hint)
 * is surfaced to the caller without retry.
 */
const RETRYABLE_AUTH_CODES = new Set([
  'TOKEN_EXPIRED',
  'invalid_token',
  'missing_token',
]);

async function doFetch(
  procedurePath: string,
  input: unknown,
  token: string,
): Promise<{ res: Response; body: unknown; clientTraceId: string }> {
  const trace = newClientTrace();
  rememberTrace(trace);
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    authorization: `Bearer ${token}`,
    traceparent: trace.traceparent,
  };
  if (_activeGroupHeader) {
    headers['x-regta-active-group'] = _activeGroupHeader;
  }
  const res = await fetch(`${REGTA_BASE}/api/v1/${procedurePath}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ json: input }),
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  return { res, body, clientTraceId: trace.traceId };
}

async function rpc<TIn extends Record<string, unknown>, TOut>(
  procedurePath: string,
  input: TIn = {} as TIn,
): Promise<TOut> {
  let token = await getToken();
  let { res, body, clientTraceId } = await doFetch(procedurePath, input, token);

  if (res.status === 401) {
    const env = parseEnvelope(body);
    if (env && env.error && RETRYABLE_AUTH_CODES.has(env.error)) {
      // Token expired (or close enough). Force a refresh — the provider
      // aggregates concurrent retries into a single mint — and retry once.
      token = await getToken({ force: true });
      ({ res, body, clientTraceId } = await doFetch(procedurePath, input, token));
    }
  }

  const serverTrace =
    res.headers.get('x-trace-id') ?? res.headers.get('traceparent') ?? clientTraceId;

  if (!res.ok) {
    const env = parseEnvelope(body);
    throw new ApiError({
      status: res.status,
      code: env?.error ?? `HTTP_${res.status}`,
      message: env?.message ?? res.statusText,
      traceId: env?.trace_id ?? serverTrace,
      data: env?.data,
    });
  }

  return (body as { json: TOut }).json;
}

export type Actor = {
  id: string;
  scope: 'user';
  ownerId: string;
  tenantId: string;
  tenantRole: 'org_admin' | 'normal';
  memberships: { groupId: string; role: 'admin' | 'normal' }[];
  tenantConfig: { defaultGroupId: string | null; normalUsersSeeGroupContracts: boolean };
  /** Effective feature set per ADR-0083, evaluated server-side at hydration. */
  features?: string[];
  epoch: number;
};

export type UserRow = {
  id: string;
  email: string;
  name: string | null;
  tenantRole: 'org_admin' | 'normal';
  clerkUserId: string;
  memberships: { groupId: string; role: 'admin' | 'normal' }[];
};

export type GroupRow = {
  id: string;
  name: string;
  notifications_mailbox: string | null;
  member_count: number;
  is_default: boolean;
  my_role: 'admin' | 'normal' | null;
  created_at: string;
};

export type GroupMember = {
  userId: string;
  email: string;
  name: string | null;
  tenantRole: 'org_admin' | 'normal';
  role: 'admin' | 'normal';
  assignedAt: string;
};

export type PendingInvitation = {
  id: string;
  email_address: string;
  status: string;
  role: string;
  created_at: number;
  public_metadata: { group_ids?: string[]; tenant_role?: string; name?: string };
};

export type TemplateListRow = {
  template_id: string;
  template_key: string;
  name: string;
  description: string | null;
  category: string;
  version_id: string;
  /** Template version slug with the leading `v` — e.g. `v1`, `v2`. */
  version: string;
  /** 'draft' only reaches entitled actors (preview_draft_templates, ADR-0083). */
  status: 'active' | 'draft';
  favorited: boolean;
  /** This version has a document-IR twin, so a beta-lane contract can be created (ADR-0099). */
  ir_available?: boolean;
};

/** Which lane renders a contract's document (ADR-0099). */
export type DocumentEngine = 'legacy' | 'ir';

export type ContractListRow = {
  id: string;
  title: string;
  status:
    | 'draft'
    | 'pending'
    | 'in_review'
    | 'awaiting_manual_signature'
    | 'sent'
    | 'viewed'
    | 'awaiting_signature'
    | 'signed'
    | 'archived'
    | 'cancelled';
  groupId: string;
  templateVersionId: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  templateKey: string;
  /** Template version slug with the leading `v` — e.g. `v1`, `v2`. */
  templateVersion: string;
  templateName: string;
  documentEngine?: DocumentEngine;
  /** True when the contract was created via R2 upload-and-extract. */
  isImported: boolean;
  /** True when an imported contract has extraction warnings, a missing
   *  property link, or an empty required-for-review field. Drives the
   *  "needs attention" pill in the list. */
  needsReview: boolean;
  /** Status of the most recent contract review, or null if never
   *  reviewed. Used by the list to differentiate "Klar för granskning"
   *  from "Granskning klar" while the contract sits in `pending`, and
   *  to surface an "Ändringar begärda" pill on draft contracts whose
   *  last review was rejected. */
  lastReviewStatus: ReviewStatus | null;
  /** Connected-contract relationship. Two-level invariant: a row is
   *  either a leaf (has parent), a root with one or more visible
   *  children, or unconnected. Children are filtered by the same
   *  ADR-0017 visibility rules as the top-level rows. */
  connection:
    | { kind: 'child'; parent: { id: string; title: string } }
    | {
        kind: 'parent';
        children: Array<{
          id: string;
          title: string;
          status: ContractListRow['status'];
        }>;
      }
    | null;
};

export type OwnerBrand = {
  ownerId: string;
  name: string;
  slug: string;
  /** Logo for white surfaces (header, letterhead). */
  brandLogoOnWhiteUrl: string | null;
  /** Logo for the brand-primary surface (side menu). */
  brandLogoOnPrimaryUrl: string | null;
  brandColors: Record<string, string> | null;
};

export type TenantBrand = {
  tenantId: string;
  name?: string | null;
  brandLogoOnWhiteUrl: string | null;
  brandLogoOnPrimaryUrl: string | null;
  brandColors: Record<string, string> | null;
};

export type BrandingResponse = {
  owner: OwnerBrand;
  tenant: TenantBrand;
};

export type BrandingPatch = {
  brand_logo_on_white_url?: string | null;
  brand_logo_on_primary_url?: string | null;
  brand_colors?: Record<string, string> | null;
};

export type SelfParty = {
  id: string;
  label: string | null;
  name: string;
  orgNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  email: string | null;
  phone: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SelfPartyInput = {
  label?: string | null;
  name: string;
  org_number?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  email?: string | null;
  phone?: string | null;
};

export type PropertyListRow = {
  id: string;
  groupId: string;
  /** Cadastral designation ("beteckning"). */
  name: string;
  street: string | null;
  postalCode: string | null;
  ort: string | null;
  kommun: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  /** Coarse count — see `properties.list` JSDoc; not visibility-filtered for normal users. */
  contractCount: number;
};

export type Property = {
  id: string;
  tenantId: string;
  groupId: string;
  name: string;
  street: string | null;
  postalCode: string | null;
  ort: string | null;
  kommun: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  softDeletedAt: string | null;
};

/** One gatuadress of a property (T-10 / ADR-0047). Ordinal 0 = primary. */
export type PropertyAddress = {
  id: string;
  street: string;
  postalCode: string | null;
  ort: string | null;
  ordinal: number;
};

export type PropertyDetail = {
  property: Property;
  /** All gatuadresser of the fastighet, ordinal order (0 = primary). */
  addresses: PropertyAddress[];
  /** Contracts linked to this property, filtered by the actor's visibility. */
  contracts: ContractListRow[];
};

/** Non-null when this contract row was produced via R2 upload-and-extract.
 *  The editor renders the original document instead of the JS template
 *  preview when this is present (per the "don't try to fill the JS
 *  preview for uploads" rule). */
export type OriginalDocument = {
  id: string;
  filename: string;
  mime: string;
  /** Short-lived presigned URL — refetch contracts.get if it expires. */
  url: string;
  expires_in: number;
  /** Forwarded from `imported_documents.extractedProperties.warnings`
   *  so the import viewer can render the "ser detta rätt ut?" prompt
   *  + the soft-dup CTAs without an extra fetch. */
  warnings: Array<{
    code: string;
    message: string;
    severity: 'info' | 'warning' | 'incomplete_required';
  }>;
};

export type LinkedProperty = {
  id: string;
  ordinal: number;
  name: string;
  /** Composed multi-line Swedish-postal address; null if unset. */
  address: string | null;
};

export type AttachmentKind = 'upload' | 'template';

export type ContractAttachment = {
  id: string;
  contractId: string;
  ordinal: number;
  kind: AttachmentKind;
  displayName: string;
  // kind = 'upload'
  uploadKey: string | null;
  uploadFilename: string | null;
  uploadMime: string | null;
  uploadBytes: number | null;
  // kind = 'template'
  templateVersionId: string | null;
  /** Resolved from `template_version_id` server-side so the editor can
   *  display the chip + look up the variableSchema. Null on upload rows. */
  templateKey: string | null;
  templateVersion: string | null;
  values: Record<string, unknown> | null;
  // Pattern A (ADR-0028 addendum): fixed-number slot binding.
  // `slotId` matches a `TemplateMetadata.attachmentSlots[*].id`;
  // `slotFixedNumber` is the slot's `fixedNumber` resolved server-side
  // for convenience. Null on freeform / off-slot rows.
  slotId: string | null;
  slotFixedNumber: number | null;
  /** Unified "Bilaga N" number resolved server-side (ADR-0028 addendum). */
  number: number | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SigningMethod = 'scrive' | 'manual';

/** Active signing attempt as exposed on ContractDetail (per the dual-workflow model). */
export type ActiveSigning = {
  id: string;
  method: SigningMethod;
  state: string;
  attempt: number;
  monitoringDisabled: boolean;
  scriveDocumentId: string | null;
  sentPdfS3Key: string | null;
  signedPdfS3Key: string | null;
  signingDueAt: string | null;
  signedAt: string | null;
  sealedAt: string | null;
};

/** SE BankID display-name order state (mirrors the admin portal). */
export type BankIdOrderState = 'not_ordered' | 'order_submitted' | 'active' | 'rejected';

/** Signing readiness — method availability + per-method preflight hints. */
export type SigningReadiness = {
  scrive_provisioned: boolean;
  available_methods: SigningMethod[];
  forced_method: SigningMethod | null;
  signing_parties: number;
  missing_party_emails: Array<{ party_id: string; role: string; ordinal: number; name: string }>;
  contract_ready: boolean;
  status: string;
  /**
   * Tenant's SE BankID order state. Signing with BankID is only permitted
   * once this is `active`; the send panel pairs it with the live roster to
   * block + warn when a BankID signer is present on a non-active order.
   */
  bankid_order_state: BankIdOrderState;
  bankid_ordered_at: string | null;
  bankid_activated_at: string | null;
};

/** One uploaded signed document in the manual workflow. */
export type ManualSignedDocument = {
  id: string;
  filename: string;
  mime: string;
  bytes: number;
  ordinal: number;
  label: string | null;
  uploaded_at: string;
  download_url: string;
};

export type ManualStateResponse = {
  contract: { id: string; status: string };
  signing: {
    id: string;
    method: SigningMethod;
    state: string;
    monitoring_disabled: boolean;
    signing_due_at: string | null;
    started_at: string | null;
    signed_at: string | null;
    completed_at: string | null;
  } | null;
  signed_documents: ManualSignedDocument[];
};

export type ContractDetail = {
  id: string;
  tenantId: string;
  groupId: string;
  templateVersionId: string;
  templateKey: string;
  templateVersion: string;
  values: Record<string, unknown>;
  title: string;
  status: ContractListRow['status'];
  documentEngine?: DocumentEngine;
  /** Primary-property cache; same as the lowest-ordinal `linkedProperties` row. */
  propertyId: string | null;
  /** Multi-property list (per ADR-0025). Empty array for templates with no property slot. */
  linkedProperties: LinkedProperty[];
  /** Bilagor bundle (per ADR-0028). Ordered by ordinal. */
  attachments: ContractAttachment[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  originalDocument: OriginalDocument | null;
  /**
   * Active signing attempt (dual-workflow model). Null until the contract
   * is first sent/prepared. Signing mechanics moved here off the contract.
   */
  activeSigning: ActiveSigning | null;
  /** Parent contract for a "connected" sub-document. Null on roots. */
  parentContractId: string | null;
  /** Brief parent summary (only when `parentContractId` is set). */
  parent: ConnectedContractSummary | null;
  /** Direct children spawned from this contract. Empty array on leaves. */
  children: ConnectedContractSummary[];
  /** Granskning approval state (ADR-0102 decisions 3 / 8). */
  approval?: ContractApproval;
};

/**
 * ADR-0102: an approval is current while the contract's latest review is
 * `approved`; any content change afterwards supersedes it.
 */
export type ReviewPolicy = { require_review_for_deviating_documents: boolean };

export type ContractApproval = {
  approved: boolean;
  approvedReviewId: string | null;
  /** An edit now would make the current approval stale — confirm first. */
  approvalWouldReset: boolean;
  /** Tenant setting: a document with adapted text needs a current approval before signing. */
  requireReviewForDeviatingDocuments: boolean;
};

/** Activity timeline event from the Scrive lifecycle. */
export type ScriveActivityEvent = {
  id: string;
  occurred_at: string;
  action: string;
  actor: {
    scope: string;
    user_id: string | null;
    service: string | null;
  };
  payload: Record<string, unknown>;
};

/** Per-party state surfaced by signing.activity. */
export type ScriveActivityParty = {
  id: string;
  role: PartyRole;
  ordinal: number;
  legal_name: string;
  email: string | null;
  id_type: PartyIdType;
  seen_at: string | null;
  signed_at: string | null;
};

/** Signer roster vocabulary (per ADR-0044). */
export type SignerType = 'firmatecknare' | 'vd' | 'ombud' | 'other';

/** Draft-time signing plan (ADR-0100). Absent option = decided at signing. */
export type SigningDraftOptions = {
  method?: SigningMethod;
  include_place_date?: boolean;
  days_to_sign?: number;
  /** `null` = no automatic reminder. */
  days_to_remind?: number | null;
  monitoring_disabled?: boolean;
  signing_order?: 'parallel' | 'sequential';
};
export type SigningDraftSigner = {
  role: 'lessor' | 'lessee' | 'guarantor' | 'witness' | 'other';
  ordinal: number;
  signer_type: SignerType | null;
  name: string | null;
};
export type SigningDraft = {
  contract_status: string;
  editable: boolean;
  export_only: boolean;
  template_methods: SigningMethod[];
  forced_method: SigningMethod | null;
  scrive_provisioned: boolean;
  slots: Array<{
    role: SigningDraftSigner['role'];
    ordinal: number;
    label: string;
    required: boolean;
    party: { id: string; legal_name: string; id_type: PartyIdType } | null;
  }>;
  signers_auto: boolean;
  signers: Array<SigningDraftSigner & { signer_ordinal: number }>;
  options: SigningDraftOptions;
  resolved: {
    method: SigningMethod | null;
    include_place_date: boolean | null;
    days_to_sign: number;
    days_to_remind: number | null;
    monitoring_disabled: boolean;
    signing_order: 'parallel' | 'sequential';
  };
  preview: {
    includePlaceDate: boolean;
    digitalStatement: boolean;
    signatories: Record<
      string,
      Array<{
        ordinal: number;
        capacity: string | null;
        name: string | null;
        entity: string | null;
        personalNumber: string | null;
      }>
    >;
  };
  updated_at: string | null;
};
export type SignerAuthMethod = 'se_bankid' | 'standard';
export type SignerHidePersonalNumber = 'none' | 'verification';

/** A signing party as surfaced to the signer-roster editor. */
export type SignerRosterParty = {
  party_id: string;
  role: 'lessor' | 'lessee' | 'guarantor' | 'witness' | 'other';
  ordinal: number;
  label: string | null;
  legal_name: string;
  id_type: PartyIdType;
  id_number: string | null;
  email: string | null;
  phone: string | null;
};

/** One signer row in the roster (get_signers response / set_signers input). */
export type SignerRosterEntry = {
  id: string;
  party_id: string;
  signer_ordinal: number;
  signer_type: SignerType | null;
  legal_name: string;
  email: string | null;
  phone: string | null;
  personal_number: string | null;
  auth_method: SignerAuthMethod;
  hide_personal_number: SignerHidePersonalNumber;
  /** Print this signer's personnummer beneath their name in the signing
   *  block — independent of the Scrive hide setting, applies in every mode. */
  show_personal_number_in_block: boolean;
};

/** Response shape for signing.get_signers. */
export type GetSignersResponse = {
  contract_status: ContractListRow['status'];
  has_explicit_roster: boolean;
  /** Template recommends printing personnummer in the signing block — seeds
   *  the per-signer default + surfaces a "rekommenderas" note in the editor. */
  recommend_pn_in_block: boolean;
  parties: SignerRosterParty[];
  signers: SignerRosterEntry[];
};

/** Per-signer live status row in signing.poll_state. */
export type ScrivePollStateSigner = {
  id: string;
  party_id: string;
  signer_ordinal: number;
  signer_type: SignerType | null;
  legal_name: string;
  email: string | null;
  seen_at: string | null;
  signed_at: string | null;
};

/** Response shape for signing.poll_state. */
export type ScrivePollStateResponse = {
  contract: {
    id: string;
    status: ContractListRow['status'];
    scrive_document_id: string | null;
    scrive_status: string | null;
    signed_at: string | null;
    sealed_at: string | null;
    last_callback_at: string | null;
    /** Signing deadline (Scrive timeout_time). */
    signing_due_at: string | null;
    /** Next automatic Scrive reminder, if configured. */
    auto_remind_at: string | null;
    /** When a manual reminder was last sent via signing.send_reminder. */
    last_reminder_sent_at: string | null;
  };
  parties: ScriveActivityParty[];
  signers: ScrivePollStateSigner[];
  scrive_reachable: boolean;
  reconcile_outcome: string | null;
};

/** Response shape for signing.activity. */
export type ScriveActivityResponse = {
  contract: {
    id: string;
    status: ContractListRow['status'];
    created_at: string;
    scrive_document_id: string | null;
    scrive_status: string | null;
    signed_at: string | null;
    sealed_at: string | null;
    last_callback_at: string | null;
  };
  parties: ScriveActivityParty[];
  timeline: ScriveActivityEvent[];
};

export type ConnectedContractSummary = {
  id: string;
  title: string;
  status: ContractListRow['status'];
  templateKey: string;
  createdAt?: string;
};

export type PropertySearchHit = {
  id: string;
  groupId: string;
  name: string;
  street: string | null;
  postalCode: string | null;
  ort: string | null;
  kommun: string | null;
};

export type SoftDupProperty = {
  id: string;
  name: string;
  street: string | null;
  postalCode: string | null;
  ort: string | null;
  kommun: string | null;
};

export type PartyIdType = 'org_no' | 'person_no' | 'foreign' | 'none';
export type PartyOrgSubtype =
  | 'ab'
  | 'kb'
  | 'hb'
  | 'municipality'
  | 'state'
  | 'foundation'
  | 'economic_assoc'
  | 'ideal_assoc'
  | 'enskild_firma'
  | 'other';
export type PartyPrivacyClass =
  | 'searchable_org'
  | 'pii_org'
  | 'pii_individual'
  | 'unknown';
export type PartyRole =
  | 'lessor'
  | 'lessee'
  | 'guarantor'
  | 'witness'
  | 'other';
export type PartyProvenance =
  | 'manual'
  | 'predefined'
  | 'self'
  | 'search'
  | 'bolagsverket'
  | 'extracted';

export type ContractParty = {
  id: string;
  contractId: string;
  role: PartyRole;
  ordinal: number;
  idType: PartyIdType;
  idNumber: string | null;
  orgSubtype: PartyOrgSubtype | null;
  privacyClass: PartyPrivacyClass;
  legalName: string;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  provenance: PartyProvenance;
  createdAt: string;
  updatedAt: string;
  /** Set when Scrive's webhook reports this party signed (per R3b). */
  signedAt: string | null;
};

export type PartySnapshotInput = {
  role: PartyRole;
  ordinal?: number;
  id_type: PartyIdType;
  id_number?: string | null;
  legal_name: string;
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  provenance?: PartyProvenance;
};

export type PartySearchHit = {
  source: 'self' | 'predefined' | 'bolagsverket';
  id: string;
  legalName: string;
  idNumber: string | null;
  idType: PartyIdType;
  orgSubtype: PartyOrgSubtype | null;
  email: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
};

/** Mirrors `ExtractedPropertiesV1` from @regta/common — kept inline to
 *  avoid frontend pulling in the backend monorepo edge. Drift would
 *  manifest as a runtime parse mismatch on the polling step. */
export type ExtractionWarning = {
  code: string;
  message: string;
  severity: 'info' | 'warning' | 'incomplete_required';
};

export type ExtractedPropertiesV1 = {
  template_type: 'bostadshyra' | 'lokalhyra' | 'parkering' | 'forhandlingsoverenskommelse' | 'unknown';
  supported: boolean;
  confidence_overall: number;
  parties: Array<{
    legal_name: string;
    role: 'lessor' | 'lessee' | 'guarantor' | 'witness' | 'unknown';
    ordinal: number | null;
    id_type: 'org_no' | 'person_no' | 'foreign' | 'none';
    id_number: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    confidence: number;
  }>;
  property: {
    street: string | null;
    postal_code: string | null;
    ort: string | null;
    designation: string | null;
    unit_number: string | null;
    unit_size_sqm: number | null;
    room_count: string | null;
    floor: string | null;
  };
  dates: {
    start: string | null;
    end: string | null;
    signing: string | null;
    notice_period_months: number | null;
  };
  rent: {
    monthly_sek: number | null;
    yearly_sek: number | null;
    period: 'monthly' | 'quarterly' | 'yearly' | null;
  };
  notes: string | null;
  warnings: ExtractionWarning[];
};

export type ImportStatus =
  | 'uploaded'
  | 'queued'
  | 'extracting'
  | 'extracted'
  | 'failed_ocr_required'
  | 'failed_extract'
  | 'confirmed';

export type ImportListRow = {
  id: string;
  groupId: string;
  rawFilename: string;
  status: ImportStatus;
  templateTypeGuess: string | null;
  supported: boolean | null;
  contractId: string | null;
  batchId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ImportDetail = {
  id: string;
  group_id: string;
  raw_filename: string;
  raw_mime: string;
  raw_bytes: number;
  status: ImportStatus;
  template_type_guess: string | null;
  supported: boolean | null;
  extracted_properties: ExtractedPropertiesV1 | null;
  contract_id: string | null;
  batch_id: string | null;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type ImportBatchItem = {
  id: string;
  groupId: string;
  rawFilename: string;
  status: ImportStatus;
  templateTypeGuess: string | null;
  supported: boolean | null;
  contractId: string | null;
  errorMessage: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ImportBatchDetail = {
  id: string;
  group_id: string;
  label: string | null;
  total_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items: ImportBatchItem[];
};

export type ImportBatchCounts = {
  total: number;
  extracted: number;
  failed: number;
  confirmed: number;
  in_progress: number;
};

export type ImportBatchListRow = {
  id: string;
  groupId: string;
  label: string | null;
  totalCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  counts: ImportBatchCounts;
};

/** `superseded`: an approval made stale by a later change (ADR-0102 decision 3). */
export type ReviewStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'superseded';

export type ReviewerCandidate = {
  id: string;
  email: string;
  name: string | null;
  tenantRole: 'org_admin' | 'normal';
};

export type ReviewUserRef = {
  id: string;
  name: string | null;
  email: string | null;
};

export type ContractReview = {
  id: string;
  contractId: string;
  status: ReviewStatus;
  requestComment: string | null;
  responseComment: string | null;
  requestedAt: string;
  respondedAt: string | null;
  reviewer: ReviewUserRef;
  requester: ReviewUserRef;
};

export type MyPendingReviewRow = {
  reviewId: string;
  contractId: string;
  groupId: string;
  title: string;
  status: ContractListRow['status'];
  templateKey: string;
  templateName: string;
  templateVersion: string;
  requestComment: string | null;
  requestedAt: string;
  requester: ReviewUserRef;
};

export type CreatePropertyResult = {
  property: Property;
  warnings: { soft_duplicates?: SoftDupProperty[] } | null;
};

// ---------- R5 lifecycle notifications --------------------------------------

export type NotificationKind =
  | 'termination_notice'
  | 'negotiation_window'
  | 'lease_end'
  | 'agreement_end'
  | 'signature_overdue'
  | 'custom';

export type NotificationLead = '3mo' | '1mo' | '1wk' | 'custom';

/** Lifecycle phase the row belongs to (post_signing | in_flight). */
export type NotificationPhase = 'post_signing' | 'in_flight';

/** Preview row — what WOULD materialize at the next phase transition. */
export type NotificationPreview = {
  ruleKey: string;
  kind: NotificationKind;
  phase: NotificationPhase;
  leadLabel: NotificationLead;
  triggerAt: string;
  fireAt: string;
  label: string;
};

export type ContractNotification = {
  id: string;
  contractId: string;
  tenantId: string;
  groupId: string;
  phase: NotificationPhase;
  kind: NotificationKind;
  leadLabel: NotificationLead;
  triggerAt: string;
  fireAt: string;
  acknowledgedAt: string | null;
  acknowledgedByUserId: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

/** Bell dropdown row shape — joins minimal contract context for click-through. */
export type DueNotificationRow = {
  id: string;
  contractId: string;
  kind: NotificationKind;
  phase: NotificationPhase;
  leadLabel: NotificationLead;
  triggerAt: string;
  fireAt: string;
  meta: Record<string, unknown>;
  contractTitle: string;
  contractStatus: string;
  contractGroupId: string;
  templateKey: string;
  templateVersion: string;
};

/** Dashboard widget row shape — same as Due minus the template hint. */
export type UpcomingNotificationRow = {
  id: string;
  contractId: string;
  kind: NotificationKind;
  phase: NotificationPhase;
  leadLabel: NotificationLead;
  triggerAt: string;
  fireAt: string;
  meta: Record<string, unknown>;
  contractTitle: string;
  contractStatus: string;
  contractGroupId: string;
};

/** "Avtal under bearbetning" page window selector. */
export type ContractReminderWindow = 'upcoming' | 'due' | 'past';

/**
 * One row per contract — primary reminder for the chosen window, plus
 * a count of other reminders for the same contract in the same window.
 */
export type ContractWithFirstReminder = {
  id: string;
  contractId: string;
  kind: NotificationKind;
  phase: NotificationPhase;
  leadLabel: NotificationLead;
  triggerAt: string;
  fireAt: string;
  acknowledgedAt: string | null;
  meta: Record<string, unknown>;
  contractTitle: string;
  contractStatus: string;
  contractGroupId: string;
  reminderCount: number;
};

// ---------- Översikt portfolio summary (REGTA-56) ---------------------------

export type PortfolioCounts = {
  active: number;
  draft: number;
  review: number;
  signed: number;
};

export type PortfolioReason =
  | { kind: 'frist'; triggerAt: string; notificationKind: NotificationKind }
  | { kind: 'in_review'; since: string }
  | { kind: 'ready_to_sign'; since: string }
  | { kind: 'reminder_sent'; lastReminderAt: string };

/** Statuses that can appear in the portfolio card's priority list —
 * mirrors PORTFOLIO_STATUSES in regta-backend overview.ts. */
export type PortfolioContractStatus =
  | 'draft'
  | 'pending'
  | 'in_review'
  | 'awaiting_manual_signature'
  | 'sent'
  | 'viewed'
  | 'awaiting_signature'
  | 'signed';

export type PortfolioTopContract = {
  contractId: string;
  title: string;
  contractNumber: string | null;
  status: PortfolioContractStatus;
  reason: PortfolioReason;
};

export type PortfolioOverview = {
  counts: PortfolioCounts;
  frister_within_30: number;
  top_contracts: PortfolioTopContract[];
};

/**
 * One NDJSON frame streamed by the AI contract assistant (ADR-0076). Mirrors
 * the backend `AgentFrame` (apps/regta-backend/src/services/claude.ts).
 */
export type ChatFrame =
  | { type: 'text'; text: string }
  | { type: 'tool_call'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; id: string; name: string; ok: boolean }
  // A write the model wants to run — the turn pauses until the user approves
  // and we resend with `approvals` (ADR-0076 Phase 2).
  | { type: 'confirm_required'; id: string; name: string; input: unknown; summary: string }
  | { type: 'done'; stop_reason: string | null }
  | { type: 'error'; code: string; message: string; correlation_id: string };

/** Content blocks the SPA replays for a paused assistant turn (text + tool_use). */
export type ChatContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown };

export type ChatTurnMessage =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string | ChatContentBlock[] };

/** A user's decision on a proposed write, echoed on the resume request. */
export type ChatApproval = { tool_use_id: string; decision: 'allow' | 'deny'; reason?: string };

export type IrDiagnostic = {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  instance_id: string | null;
  block_ref: string | null;
  slot_key: string | null;
};

export type IrPreviewPart = {
  key: string;
  title: string;
  html: string;
  letterheadHtml: string;
  footerHtml: string;
};

/** `contracts.ir_preview` — the compiled beta document (ADR-0099). */
export type IrPreview = {
  documentEngine: DocumentEngine;
  source: 'live' | 'issued';
  revisionId: string | null;
  revision: number | null;
  html: string;
  letterheadHtml: string;
  footerHtml: string;
  css: string;
  parts: IrPreviewPart[];
  diagnostics: IrDiagnostic[];
  /** Null once the contract is frozen (an issued revision is served). */
  autofill: {
    autoKeys: string[];
    manualKeys: string[];
    autoValues: Record<string, unknown>;
  } | null;
  document: unknown;
  /** The action log replayed over this compile ("Anpassa text"). */
  finetune?: Pick<FinetuneView, 'groups' | 'conflicts' | 'deviatesFromStandard' | 'deviationLabel' | 'editable'> | null;
  /**
   * Template bilagor (ADR-0102 addendum 3), each its own document in
   * Bilagor order — shown after the main document. Absent on older servers.
   */
  attachments?: IrPreviewAttachment[];
  /** Main + bilagor in one string; each region carries `data-document-key` (`main` / attachment id). */
  bundleHtml?: string;
};

/** One template bilaga of a beta contract, compiled as its own document. */
export type IrPreviewAttachment = {
  attachmentId: string;
  templateKey: string;
  templateVersion: string;
  displayName: string;
  number: number | null;
  /** "Bilaga N – namn". */
  title: string;
  html: string;
  letterheadHtml: string;
  footerHtml: string;
  diagnostics: IrDiagnostic[];
  autofill: { autoKeys: string[]; manualKeys: string[]; autoValues: Record<string, unknown> } | null;
  /** The bilaga's values as compiled (mapped fields resolved from the main document). Null once frozen. */
  values: Record<string, unknown> | null;
  document: unknown;
  finetune: { lastSeq: number; deviatesFromStandard: boolean; deviationLabel: string | null; conflicts: number };
};

export type IrRevision = {
  id: string;
  revision: number;
  kind: 'compiled' | 'patched';
  status: 'draft' | 'issued' | 'signed' | 'superseded';
  documentKey: string;
  documentVersion: string;
  contentHash: string;
  compiledAt: string;
  issuedAt: string | null;
  supersededAt: string | null;
};

export type IrRevisionList = {
  documentEngine: DocumentEngine;
  editable: boolean;
  revisions: IrRevision[];
};

/**
 * "Anpassa text" (ADR-0102) — the contract's append-only
 * action log replayed over the current compile (ADR-0092 / ADR-0099).
 * Text fields are contract content: display only, never log.
 */
export type FinetuneView = {
  log: ActionLog;
  lastSeq: number;
  editable: boolean;
  report: ReplayReportEntry[];
  conflicts: ReplayReportEntry[];
  /** One row per changed node: versions, head, superseded entries, redo. */
  groups: ChangeGroup[];
  deviatesFromStandard: boolean;
  deviationLabel: string | null;
  deviatingInstances: string[];
  detachedValues: Array<{ patchId: string; address: string; sourceKey?: string; text: string | null }>;
  removedSections: Array<{ ref: string; patchId: string }>;
  changes: PatchChange[] | null;
  trackedHtml: string | null;
  trackCss: string | null;
  /** With `track`: the resolved review IR (ghosts + edit marks). */
  trackedDocument: ResolvedDocumentIR | null;
  /** `review` while in_review: content entries become suggestions (ADR-0102 addendum 2). */
  appendMode?: 'adapt' | 'review';
  /** Every suggestion of this document's log, decided ones included. */
  suggestions?: SuggestionInfo[];
  /** Open (`proposed` + `stale`) suggestions of this document. */
  openSuggestions?: number;
  /**
   * Live contracts: the inputs for a client-side `createAdaptationSession`
   * (ADR-0102 addendum) — `getComposition(templateKey, templateVersion,
   * values)` + `createCompileContext(context)` + the Regta block registry,
   * over `log`. `values` are post-autofill, own `custom_…` values included.
   * `null` once issued. Contract content: display only, never log.
   */
  session?: {
    templateKey: string;
    templateVersion: string;
    values: Record<string, unknown>;
    context: CompileContextInput;
  } | null;
};

export type ReviewAnchorKind = 'action' | 'node' | 'field' | 'document';
export type ReviewCommentKind = 'comment' | 'field_request';

export type ReviewComment = { id: string; body: string; author_user_id: string; created_at: string };

/** A comment thread or field request. Bodies and values are contract content: display only, never log. */
export type ReviewThread = ReviewComment & {
  /** null = main document, otherwise the bilaga. */
  attachment_id: string | null;
  anchor_kind: ReviewAnchorKind;
  /** Seq (as text) / node address / field key; null for 'document'. */
  anchor: string | null;
  kind: ReviewCommentKind;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  field_value_at_request: unknown;
  current_value: unknown;
  value_changed: boolean;
  replies: ReviewComment[];
};

export type ReviewThreadList = {
  threads: ReviewThread[];
  /** Over every document of the contract. */
  open_threads: number;
  open_field_requests: number;
  writable: boolean;
};

/** One action to append: an entry without `seq` / `author` / `at` (server-assigned). */
export type FinetuneAction = NewActionLogEntry extends infer E
  ? E extends NewActionLogEntry
    ? Omit<E, 'author' | 'at'>
    : never
  : never;

export const tenantApi = {
  whoami: () => rpc<Record<string, never>, { actor: Actor }>('meta/whoami'),
  templates: {
    list: () =>
      rpc<Record<string, never>, { templates: TemplateListRow[] }>(
        'templates/list',
      ),
    get: (
      input: { template_key: string; version: string },
    ) =>
      rpc<
        typeof input,
        {
          template_key: string;
          version: string;
          version_id: string;
          status: string;
          ir_available?: boolean;
          metadata: unknown;
          variable_schema: unknown;
        }
      >('templates/get', input),
    favorite: (input: { template_id: string }) =>
      rpc<{ template_id: string }, { ok: true }>('templates/favorite', input),
    unfavorite: (input: { template_id: string }) =>
      rpc<{ template_id: string }, { ok: true }>('templates/unfavorite', input),
  },
  contracts: {
    list: (input?: { q?: string }) =>
      rpc<{ q?: string }, { contracts: ContractListRow[] }>(
        'contracts/list',
        input ?? {},
      ),
    get: (id: string) =>
      rpc<{ id: string }, { contract: ContractDetail }>('contracts/get', { id }),
    create_from_template: (
      input: {
        template_version_id: string;
        /** Optional — the active-group header is the canonical source. */
        group_id?: string;
        title?: string;
        initial_values?: Record<string, unknown>;
        /** For "connected" sub-contracts; the parent must be a root. */
        parent_contract_id?: string;
        /** `ir` needs `ir_available` on the version; omitted ⇒ legacy. */
        engine?: DocumentEngine;
      },
    ) =>
      rpc<typeof input, { contract_id: string }>('contracts/create_from_template', input),
    reserve_number: () =>
      rpc<Record<string, never>, { number: string }>('contracts/reserve_number', {}),
    ir_preview: (input: {
      contractId: string;
      values?: Record<string, unknown>;
      /** Unsaved editor party edits, same shape as `save_draft` (preview only, nothing is written). */
      parties_add?: PartySnapshotInput[];
      parties_remove?: string[];
      /** Unsaved template-bilaga values by attachment id (preview only). */
      attachmentValues?: Record<string, Record<string, unknown>>;
    }) => rpc<typeof input, IrPreview>('contracts/ir_preview', input),
    set_autofill_mode: (input: { contractId: string; fieldKey: string; mode: 'auto' | 'manual' }) =>
      rpc<typeof input, { values: Record<string, unknown>; mode: 'auto' | 'manual' }>(
        'contracts/set_autofill_mode',
        input,
      ),
    ir_revisions: (input: { contractId: string }) =>
      rpc<typeof input, IrRevisionList>('contracts/ir_revisions', input),
    /** `attachmentId` scopes to a template bilaga's own document + log (null/omitted = main). */
    finetune_get: (input: { contractId: string; track?: boolean; attachmentId?: string | null }) =>
      rpc<typeof input, FinetuneView>('contracts/finetune_get', input),
    /** CONFLICT `finetune_stale_log` when `expectedLastSeq` is out of date (per document). */
    finetune_append: (input: {
      contractId: string;
      expectedLastSeq: number;
      actions: FinetuneAction[];
      track?: boolean;
      attachmentId?: string | null;
    }) =>
      rpc<typeof input, FinetuneView & { appended: number[] }>('contracts/finetune_append', input),
    update_draft: (
      input: { id: string; values: Record<string, unknown>; title?: string },
    ) => rpc<typeof input, { ok: boolean }>('contracts/update_draft', input),
    /**
     * R2 imports only — narrow update of import-tagged fields on a
     * signed-from-upload contract. Server filters keys by template
     * `scopes.imported`; non-imported keys are dropped (returned in
     * `rejected`) so the client can't sneak clause edits through.
     */
    update_imported_fields: (
      input: { id: string; values: Record<string, unknown> },
    ) =>
      rpc<typeof input, { ok: true; accepted: number; rejected: string[] }>(
        'contracts/update_imported_fields',
        input,
      ),
    /**
     * Single transactional save for the editor — flushes the user's
     * dirty values + the local party diff (adds / updates / removes)
     * in one round-trip. Replaces the per-mutation party endpoints
     * during in-editor drafting.
     */
    save_draft: (
      input: {
        id: string;
        values?: Record<string, unknown>;
        title?: string;
        parties_add?: PartySnapshotInput[];
        parties_update?: (Partial<PartySnapshotInput> & { id: string })[];
        parties_remove?: string[];
      },
    ) => rpc<typeof input, { ok: boolean }>('contracts/save_draft', input),
    /**
     * Promote a draft to `pending`. Returns `{ ok: false, missing_fields,
     * missing_slots }` when required fields/party slots are still empty;
     * the editor uses that payload to surface which slots/inputs are blocking.
     */
    /**
     * `cancel_open_review` is required from in_review (else 400
     * `review_still_open`); `reset_approval` supersedes a current approval
     * now (field navigation, ADR-0102 addendum 2).
     */
    revert_to_draft: (input: { id: string; cancel_open_review?: boolean; reset_approval?: boolean }) =>
      rpc<
        typeof input,
        {
          ok: true;
          status: 'draft';
          approval_superseded?: boolean;
          superseded_review_ids?: string[];
          cancelled_review_id?: string | null;
          approval_current?: boolean;
        }
      >('contracts/revert_to_draft', input),
    /**
     * Archive a pre-signing contract (draft / pending / in_review).
     * Server refuses if an active review row exists; for now the signing
     * flow has no archive button — see procedures/contracts.ts.
     */
    archive: (input: { id: string }) =>
      rpc<typeof input, { ok: true }>('contracts/archive', input),
    /**
     * Delete an uploaded (imported) contract and dispose of its
     * uploaded document (REGTA-77). Soft-delete + eager S3 disposal;
     * refused for drafted contracts (`not_imported`) and when live
     * connected children exist (`delete_children_first`).
     */
    delete: (input: { id: string }) =>
      rpc<typeof input, { ok: true }>('contracts/delete', input),
    /**
     * Search same-templateKey contracts the actor can view, for the
     * "Kopiera från befintligt avtal" picker. Excludes the target,
     * applies the standard visibility filter, ranked by recency.
     */
    list_copy_sources: (
      input: { contract_id: string; q?: string },
    ) =>
      rpc<
        typeof input,
        {
          contracts: Array<{
            id: string;
            title: string;
            status: ContractListRow['status'];
            groupId: string;
            createdBy: string | null;
            updatedAt: string;
            templateKey: string;
            templateVersion: string;
          }>;
        }
      >('contracts/list_copy_sources', input),
    /**
     * Copy selected pieces (values, parties, property, attachments)
     * from a same-template source contract into a draft target. PII
     * parties are dropped server-side regardless of selection.
     */
    copy_from: (
      input: {
        target_contract_id: string;
        source_contract_id: string;
        options: {
          party_ids: string[];
          copy_property: boolean;
          copy_linked_properties: boolean;
          attachment_ids: string[];
          value_field_keys: string[];
          /** ADR-0100: copy the signing plan (planned signers + options). */
          copy_signing?: boolean;
        };
      },
    ) =>
      rpc<
        typeof input,
        {
          ok: true;
          summary: {
            values_copied: number;
            parties_copied: number;
            property_copied: boolean;
            linked_properties_copied: number;
            attachments_copied: number;
            parties_skipped_pii: number;
            parties_skipped_slot_taken: number;
            attachments_skipped: number;
            signing_plan_copied?: boolean;
          };
        }
      >('contracts/copy_from', input),
    promote_to_pending: (input: { id: string }) =>
      rpc<
        typeof input,
        {
          ok: boolean;
          missing_fields: string[];
          missing_slots: string[];
          missing_attachment_slots: string[];
          /** Unresolved block validation errors (ADR-0101); beta contracts only. */
          validation_errors?: Array<{
            id: string;
            rule_id: string;
            instance_id: string;
            slot_key: string;
            severity: 'error' | 'warning';
            message: string;
          }>;
        }
      >('contracts/promote_to_pending', input),
    link_property: (input: { id: string; property_id: string }) =>
      rpc<
        typeof input,
        { ok: true; values: Record<string, unknown>; title: string }
      >('contracts/link_property', input),
    unlink_property: (input: { id: string }) =>
      rpc<
        typeof input,
        { ok: true; values: Record<string, unknown>; title: string }
      >('contracts/unlink_property', input),
    /**
     * Fetch the server-rendered PDF for a contract. Binary endpoint —
     * sits outside the oRPC handler (see apps/regta-backend/src/routes/
     * contracts-render.ts). Returns a Blob the caller can wrap with
     * URL.createObjectURL for iframe display.
     */
    render: async (
      id: string,
      /**
       * Manual signing: download a single part instead of the merged PDF.
       * `{ part: 'head' }` = head document; `{ part: 'bilaga', n }` = the
       * Nth bilaga (0-based); `{ part: 'doc', key }` = one document part of
       * a multi-document template (per ADR-0057). Omit for the merged
       * frozen document.
       */
      part?:
        | { part: 'head' }
        | { part: 'bilaga'; n: number }
        | { part: 'doc'; key: string },
    ): Promise<Blob> => {
      const trace = newClientTrace();
      rememberTrace(trace);
      const qs = part
        ? `?part=${part.part}` +
          (part.part === 'bilaga' ? `&n=${part.n}` : '') +
          (part.part === 'doc' ? `&key=${encodeURIComponent(part.key)}` : '')
        : '';
      const doRenderFetch = async (tok: string): Promise<Response> =>
        fetch(`${REGTA_BASE}/api/v1/contracts/${id}/render${qs}`, {
          method: 'GET',
          headers: {
            authorization: `Bearer ${tok}`,
            traceparent: trace.traceparent,
          },
        });
      let tok = await getToken();
      let res = await doRenderFetch(tok);
      if (res.status === 401) {
        tok = await getToken({ force: true });
        res = await doRenderFetch(tok);
      }
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        const serverTrace = res.headers.get('x-trace-id') ?? trace.traceId;
        throw new ApiError({
          status: res.status,
          code: `HTTP_${res.status}`,
          message: detail.slice(0, 200) || res.statusText,
          traceId: serverTrace,
        });
      }
      return await res.blob();
    },
    /**
     * Bulk export of the contracts matching one or more template versions
     * (+ the same free-text `q` the list uses), scoped to the caller's
     * visibility and the active group. Binary endpoint (see apps/regta-
     * backend/src/routes/contracts-export.ts): a CSV bundle (contracts /
     * parties / properties / codebook / per-table companions) or, with
     * `format:'xlsx'`, an Excel workbook. Returns the Blob plus the server's
     * row count, truncation flag, and the server-supplied download filename.
     */
    export_csv: async (input: {
      templateVersionIds: string[];
      q?: string;
      /** 'labels' → human/readable headers + option labels + Ja/Nej.
       *  Omitted/'codes' → the ADR-0067 canonical code-headed export. */
      headers?: 'codes' | 'labels';
      /** 'xlsx' → Excel workbook; default 'csv' → the CSV/zip bundle. */
      format?: 'csv' | 'xlsx';
    }): Promise<{ blob: Blob; count: number; truncated: boolean; filename: string }> => {
      const trace = newClientTrace();
      rememberTrace(trace);
      const params = new URLSearchParams({
        template_version_id: input.templateVersionIds.join(','),
      });
      if (input.q) params.set('q', input.q);
      if (input.headers === 'labels') params.set('headers', 'labels');
      if (input.format === 'xlsx') params.set('format', 'xlsx');
      const doExportFetch = async (tok: string): Promise<Response> => {
        const headers: Record<string, string> = {
          authorization: `Bearer ${tok}`,
          traceparent: trace.traceparent,
        };
        if (_activeGroupHeader) headers['x-regta-active-group'] = _activeGroupHeader;
        return fetch(`${REGTA_BASE}/api/v1/contracts/export.csv?${params.toString()}`, {
          method: 'GET',
          headers,
        });
      };
      let tok = await getToken();
      let res = await doExportFetch(tok);
      if (res.status === 401) {
        tok = await getToken({ force: true });
        res = await doExportFetch(tok);
      }
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        const serverTrace = res.headers.get('x-trace-id') ?? trace.traceId;
        throw new ApiError({
          status: res.status,
          code: `HTTP_${res.status}`,
          message: detail.slice(0, 200) || res.statusText,
          traceId: serverTrace,
        });
      }
      const truncated = res.headers.get('x-regta-export-truncated') === 'true';
      const count = Number(res.headers.get('x-regta-export-count') ?? '0');
      const disposition = res.headers.get('content-disposition') ?? '';
      const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? 'export.zip';
      return { blob: await res.blob(), count, truncated, filename };
    },
  },
  branding: {
    get: () =>
      rpc<Record<string, never>, BrandingResponse>('branding/get'),
    update: (input: BrandingPatch) =>
      rpc<typeof input, { tenant: TenantBrand }>('branding/update', input),
  },
  properties: {
    list: () =>
      rpc<Record<string, never>, { properties: PropertyListRow[] }>('properties/list'),
    search: (input: { q?: string; limit?: number } = {}) =>
      rpc<typeof input, { properties: PropertySearchHit[] }>('properties/search', input),
    get: (id: string) =>
      rpc<{ id: string }, PropertyDetail>('properties/get', { id }),
    create: (
      input: {
        /** Optional — active-group header is canonical. */
        group_id?: string;
        name: string;
        street?: string | null;
        postal_code?: string | null;
        ort?: string | null;
        kommun?: string | null;
        description?: string | null;
      },
    ) => rpc<typeof input, CreatePropertyResult>('properties/create', input),
    update: (
      input: {
        id: string;
        name?: string;
        street?: string | null;
        postal_code?: string | null;
        ort?: string | null;
        kommun?: string | null;
        description?: string | null;
      },
    ) => rpc<typeof input, { property: Property }>('properties/update', input),
    archive: (input: { id: string }) =>
      rpc<typeof input, { ok: boolean }>('properties/archive', input),
    addresses: {
      /** Replace the property's full address list. Index 0 = primary. */
      set: (input: {
        property_id: string;
        addresses: Array<{ street: string; postal_code?: string | null; ort?: string | null }>;
      }) =>
        rpc<typeof input, { addresses: PropertyAddress[] }>(
          'properties/addresses/set',
          input,
        ),
    },
  },
  contract_attachments: {
    list: (input: { contract_id: string }) =>
      rpc<typeof input, { attachments: ContractAttachment[] }>(
        'contract_attachments/list',
        input,
      ),
    upload_url: (
      input: {
        contract_id: string;
        filename: string;
        mime: string;
        bytes: number;
        slot_id?: string | null;
      },
    ) =>
      rpc<
        typeof input,
        {
          attachment_id: string;
          upload_url: string;
          upload_key: string;
          expires_in: number;
        }
      >('contract_attachments/upload_url', input),
    create: (
      input: {
        contract_id: string;
        attachment_id: string;
        upload_key: string;
        filename: string;
        mime: string;
        bytes: number;
        display_name?: string;
        slot_id?: string | null;
      },
    ) =>
      rpc<typeof input, { attachment: ContractAttachment }>(
        'contract_attachments/create',
        input,
      ),
    assign_slot: (
      input: { id: string; slot_id: string | null },
    ) =>
      rpc<typeof input, { ok: boolean }>(
        'contract_attachments/assign_slot',
        input,
      ),
    update: (input: { id: string; display_name: string }) =>
      rpc<typeof input, { ok: boolean }>(
        'contract_attachments/update',
        input,
      ),
    remove: (input: { id: string }) =>
      rpc<typeof input, { ok: boolean }>(
        'contract_attachments/remove',
        input,
      ),
    reorder: (input: { contract_id: string; ids: string[] }) =>
      rpc<typeof input, { ok: boolean }>(
        'contract_attachments/reorder',
        input,
      ),
    download_url: (input: { id: string }) =>
      rpc<
        typeof input,
        { url: string; expires_in: number; mime: string; filename: string }
      >('contract_attachments/download_url', input),
    create_from_template: (
      input: {
        contract_id: string;
        template_version_id: string;
        display_name?: string;
      },
    ) =>
      rpc<typeof input, { attachment: ContractAttachment }>(
        'contract_attachments/create_from_template',
        input,
      ),
    update_template_values: (
      input: { id: string; values: Record<string, unknown> },
    ) =>
      rpc<typeof input, { ok: boolean }>(
        'contract_attachments/update_template_values',
        input,
      ),
    resync_from_parent: (input: { id: string }) =>
      rpc<
        typeof input,
        { ok: boolean; values: Record<string, unknown> }
      >('contract_attachments/resync_from_parent', input),
    /** Beta lane: a bilaga field mapped from the main document (or autofilled) → auto / manual. */
    set_template_autofill_mode: (input: { id: string; field_key: string; mode: 'auto' | 'manual' }) =>
      rpc<typeof input, { ok: boolean; mode: 'auto' | 'manual'; value: unknown }>(
        'contract_attachments/set_template_autofill_mode',
        input,
      ),
  },
  contract_properties: {
    list: (input: { contract_id: string }) =>
      rpc<typeof input, { linked_properties: LinkedProperty[] }>(
        'contract_properties/list',
        input,
      ),
    add: (input: { contract_id: string; property_id: string }) =>
      rpc<typeof input, { linked_properties: LinkedProperty[] }>(
        'contract_properties/add',
        input,
      ),
    remove: (input: { contract_id: string; property_id: string }) =>
      rpc<typeof input, { linked_properties: LinkedProperty[] }>(
        'contract_properties/remove',
        input,
      ),
    reorder: (
      input: { contract_id: string; property_ids: string[] },
    ) =>
      rpc<typeof input, { linked_properties: LinkedProperty[] }>(
        'contract_properties/reorder',
        input,
      ),
    set_mode: (
      input: { contract_id: string; field_key: string; mode: 'auto' | 'manual' },
    ) =>
      rpc<typeof input, { values: Record<string, unknown> }>(
        'contract_properties/set_mode',
        input,
      ),
  },
  contract_parties: {
    list: (input: { contract_id: string }) =>
      rpc<typeof input, { parties: ContractParty[] }>('contract_parties/list', input),
    add: (
      input: PartySnapshotInput & { contract_id: string },
    ) => rpc<typeof input, { party: ContractParty }>('contract_parties/add', input),
    bulk_add: (
      input: { contract_id: string; parties: PartySnapshotInput[] },
    ) => rpc<typeof input, { parties: ContractParty[] }>('contract_parties/bulk_add', input),
    update: (
      input: { id: string } & Partial<Omit<PartySnapshotInput, 'provenance'>>,
    ) => rpc<typeof input, { party: ContractParty }>('contract_parties/update', input),
    remove: (input: { id: string }) =>
      rpc<typeof input, { ok: boolean }>('contract_parties/remove', input),
  },
  parties: {
    search: (input: { q: string; limit?: number }) =>
      rpc<typeof input, { hits: PartySearchHit[] }>('parties/search', input),
    lookupByOrgNo: (input: { org_no: string }) =>
      rpc<typeof input, { hit: PartySearchHit | null }>(
        'parties/lookupByOrgNo',
        input,
      ),
  },
  settings: {
    list_self_parties: () =>
      rpc<Record<string, never>, { self_parties: SelfParty[] }>(
        'settings/list_self_parties',
      ),
    create_self_party: (input: SelfPartyInput & { set_default?: boolean }) =>
      rpc<typeof input, { self_party: SelfParty }>('settings/create_self_party', input),
    update_self_party: (input: { id: string } & Partial<SelfPartyInput>) =>
      rpc<typeof input, { self_party: SelfParty }>('settings/update_self_party', input),
    set_default_self_party: (input: { id: string }) =>
      rpc<typeof input, { self_party: SelfParty }>('settings/set_default_self_party', input),
    /** ADR-0102 decision 8. */
    get_review_policy: () =>
      rpc<Record<string, never>, ReviewPolicy>('settings/get_review_policy', {}),
    /** Org-admins only. */
    set_review_policy: (input: ReviewPolicy) =>
      rpc<typeof input, ReviewPolicy>('settings/set_review_policy', input),
    set_sso_join_policy: (input: { policy: 'pending' | 'default_group' }) =>
      rpc<typeof input, { ok: true; policy: 'pending' | 'default_group' }>(
        'settings/set_sso_join_policy',
        input,
      ),
  },
  imports: {
    upload_url: (
      input: {
        group_id?: string;
        filename: string;
        mime: string;
        bytes: number;
        batch_id?: string;
      },
    ) =>
      rpc<
        typeof input,
        { document_id: string; upload_url: string; raw_key: string; expires_in: number }
      >('imports/upload_url', input),
    create: (
      input: {
        document_id: string;
        group_id?: string;
        raw_key: string;
        filename: string;
        mime: string;
        bytes: number;
        batch_id?: string;
      },
    ) =>
      rpc<typeof input, { document_id: string; status: 'queued' }>(
        'imports/create',
        input,
      ),
    list: () =>
      rpc<Record<string, never>, { imports: ImportListRow[] }>('imports/list'),
    get: (id: string) =>
      rpc<{ id: string }, ImportDetail>('imports/get', { id }),
    confirm: (
      input: { document_id: string; template_version_id: string; title?: string },
    ) =>
      rpc<typeof input, { contract_id: string }>('imports/confirm', input),
    discard: (input: { id: string }) =>
      rpc<typeof input, { ok: true }>('imports/discard', input),
    retry: (input: { id: string }) =>
      rpc<typeof input, { ok: true }>('imports/retry', input),
    batch_start: (
      input: { group_id?: string; total_count: number; label?: string },
    ) =>
      rpc<typeof input, { batch_id: string; group_id: string }>(
        'imports/batch_start',
        input,
      ),
    batch_get: (id: string) =>
      rpc<{ id: string }, ImportBatchDetail>('imports/batch_get', { id }),
    batch_list: () =>
      rpc<Record<string, never>, { batches: ImportBatchListRow[] }>(
        'imports/batch_list',
      ),
    batch_discard: (input: { id: string }) =>
      rpc<typeof input, { ok: true; discarded_members: number }>(
        'imports/batch_discard',
        input,
      ),
  },
  groups: {
    list: () =>
      rpc<
        Record<string, never>,
        {
          groups: GroupRow[];
          default_group_id: string | null;
          sso_join_policy: 'pending' | 'default_group';
        }
      >('groups/list'),
    members: (input: { group_id: string }) =>
      rpc<typeof input, { members: GroupMember[] }>('groups/members', input),
    create: (
      input: {
        name: string;
        initial_user_ids?: string[];
        notifications_mailbox?: string | null;
      },
    ) =>
      rpc<
        typeof input,
        {
          group: {
            id: string;
            name: string;
            notifications_mailbox: string | null;
            created_at: string;
          };
        }
      >('groups/create', input),
    add_member: (
      input: { group_id: string; user_id: string; role?: 'admin' | 'normal' },
    ) => rpc<typeof input, { ok: true }>('groups/add_member', input),
    remove_member: (input: { group_id: string; user_id: string }) =>
      rpc<typeof input, { ok: true }>('groups/remove_member', input),
    set_member_role: (
      input: { group_id: string; user_id: string; role: 'admin' | 'normal' },
    ) => rpc<typeof input, { ok: true }>('groups/set_member_role', input),
    disable: (input: { group_id: string }) =>
      rpc<typeof input, { ok: true }>('groups/disable', input),
  },
  /** Granskning comments and field requests (ADR-0102 addendum 2). Regta-only. */
  review_comments: {
    list: (input: { contract_id: string; attachment_id?: string | null; include_resolved?: boolean }) =>
      rpc<typeof input, ReviewThreadList>('review_comments/list', input),
    create: (input: {
      contract_id: string;
      attachment_id?: string | null;
      anchor_kind: ReviewAnchorKind;
      anchor?: string | number | null;
      kind?: ReviewCommentKind;
      body: string;
    }) => rpc<typeof input, { thread: ReviewThread }>('review_comments/create', input),
    reply: (input: { contract_id: string; thread_id: string; body: string }) =>
      rpc<typeof input, { thread: ReviewThread }>('review_comments/reply', input),
    resolve: (input: { contract_id: string; thread_id: string }) =>
      rpc<typeof input, { thread: ReviewThread }>('review_comments/resolve', input),
    reopen: (input: { contract_id: string; thread_id: string }) =>
      rpc<typeof input, { thread: ReviewThread }>('review_comments/reopen', input),
  },
  reviews: {
    list_potential_reviewers: (input: { contract_id: string }) =>
      rpc<typeof input, { reviewers: ReviewerCandidate[] }>(
        'reviews/list_potential_reviewers',
        input,
      ),
    request: (
      input: { contract_id: string; reviewer_user_id: string; comment?: string },
    ) =>
      rpc<typeof input, { review_id: string }>('reviews/request', input),
    approve: (input: { review_id: string; comment?: string }) =>
      rpc<typeof input, { ok: true }>('reviews/approve', input),
    reject: (input: { review_id: string; comment: string }) =>
      rpc<typeof input, { ok: true }>('reviews/reject', input),
    cancel: (input: { review_id: string }) =>
      rpc<typeof input, { ok: true }>('reviews/cancel', input),
    list_my_pending: () =>
      rpc<Record<string, never>, { reviews: MyPendingReviewRow[] }>(
        'reviews/list_my_pending',
      ),
    list_for_contract: (input: { contract_id: string }) =>
      rpc<typeof input, { reviews: ContractReview[]; approval?: ContractApproval }>(
        'reviews/list_for_contract',
        input,
      ),
  },
  notifications: {
    list_by_contract: (input: { contract_id: string }) =>
      rpc<typeof input, { notifications: ContractNotification[] }>(
        'notifications/list_by_contract',
        input,
      ),
    preview: (input: { contract_id: string }) =>
      rpc<
        typeof input,
        { previews: NotificationPreview[]; contract_status: string }
      >('notifications/preview', input),
    list_due: (input?: { limit?: number }) =>
      rpc<{ limit?: number }, { notifications: DueNotificationRow[]; total: number }>(
        'notifications/list_due',
        input ?? {},
      ),
    list_upcoming: (input?: { within_days?: number; limit?: number }) =>
      rpc<
        { within_days?: number; limit?: number },
        {
          notifications: UpcomingNotificationRow[];
          counts: { within30: number; within60: number; within90: number };
        }
      >('notifications/list_upcoming', input ?? {}),
    list_grouped_by_contract: (
      input?: { window?: ContractReminderWindow; limit?: number },
    ) =>
      rpc<
        { window?: ContractReminderWindow; limit?: number },
        { contracts: ContractWithFirstReminder[]; total: number }
      >('notifications/list_grouped_by_contract', input ?? {}),
    counts_by_window: () =>
      rpc<
        Record<string, never>,
        { upcoming: number; due: number; past: number }
      >('notifications/counts_by_window', {}),
    acknowledge: (input: { notification_id: string }) =>
      rpc<typeof input, { notification_id: string; acknowledged_at: string }>(
        'notifications/acknowledge',
        input,
      ),
    acknowledge_group: (input: { notification_ids: string[] }) =>
      rpc<typeof input, { acknowledged: number; acknowledged_at: string }>(
        'notifications/acknowledge_group',
        input,
      ),
    snooze: (input: { notification_ids: string[]; snooze_until: string }) =>
      rpc<typeof input, { snoozed: number; fire_at: string }>(
        'notifications/snooze',
        input,
      ),
    edit_date: (input: { notification_id: string; fire_at: string }) =>
      rpc<typeof input, { notification_id: string; fire_at: string }>(
        'notifications/edit_date',
        input,
      ),
    delete: (input: { notification_id: string }) =>
      rpc<typeof input, { notification_id: string; deleted: true }>(
        'notifications/delete',
        input,
      ),
    add_custom: (
      input: { contract_id: string; fire_at: string; label: string },
    ) =>
      rpc<typeof input, { notification_id: string }>(
        'notifications/add_custom',
        input,
      ),
    disable_for_contract: (input: { contract_id: string }) =>
      rpc<typeof input, { deleted: number }>(
        'notifications/disable_for_contract',
        input,
      ),
    enable_for_contract: (input: { contract_id: string }) =>
      rpc<typeof input, { scheduled: number }>(
        'notifications/enable_for_contract',
        input,
      ),
  },
  overview: {
    portfolio: () =>
      rpc<Record<string, never>, PortfolioOverview>('overview/portfolio'),
  },
  hyreslista: {
    /**
     * Auto-map an uploaded spreadsheet's columns onto our target columns
     * via Bedrock (Nova), with a deterministic heuristic fallback. Returns
     * one mapping per target column; `source` tells which path produced it.
     */
    suggest_mapping: (input: {
      target_columns: Array<{
        key: string;
        label: string;
        kind?: 'text' | 'number' | 'currency';
        description?: string;
      }>;
      input_headers: string[];
      sample_rows: string[][];
    }) =>
      rpc<typeof input, { mappings: ColumnMapping[]; source: 'bedrock' | 'heuristic' }>(
        'hyreslista/suggest_mapping',
        input,
      ),
  },
  /**
   * tenant.chat.* — AI contract assistant (ADR-0076). `status` gates whether
   * the panel is offered at all; `streamTurn` runs one assistant turn,
   * streaming NDJSON frames to `onFrame`. The turn goes through the raw
   * streaming route (not oRPC) — POST `{messages}` directly, read the body as
   * a stream. Mirrors the `render`/`export_csv` token-refresh pattern and
   * attaches the active-group header like every other tenant call.
   */
  chat: {
    status: () =>
      rpc<
        Record<string, never>,
        { available: boolean; read_enabled: boolean; write_enabled: boolean }
      >('chat/status', {}),
    streamTurn: async (
      input: { messages: ChatTurnMessage[]; approvals?: ChatApproval[] },
      onFrame: (frame: ChatFrame) => void,
      opts?: { signal?: AbortSignal },
    ): Promise<void> => {
      const trace = newClientTrace();
      rememberTrace(trace);
      const doStreamFetch = (tok: string): Promise<Response> => {
        const headers: Record<string, string> = {
          'content-type': 'application/json',
          authorization: `Bearer ${tok}`,
          traceparent: trace.traceparent,
        };
        if (_activeGroupHeader) headers['x-regta-active-group'] = _activeGroupHeader;
        return fetch(`${REGTA_BASE}/api/v1/chat/stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
          ...(opts?.signal ? { signal: opts.signal } : {}),
        });
      };

      let tok = await getToken();
      let res = await doStreamFetch(tok);
      if (res.status === 401) {
        tok = await getToken({ force: true });
        res = await doStreamFetch(tok);
      }
      if (!res.ok || !res.body) {
        const detail = await res.text().catch(() => '');
        throw new ApiError({
          status: res.status,
          code: `HTTP_${res.status}`,
          message: detail.slice(0, 200) || res.statusText,
          traceId: res.headers.get('x-trace-id') ?? trace.traceId,
        });
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      const flushLine = (line: string) => {
        const trimmed = line.trim();
        if (trimmed) onFrame(JSON.parse(trimmed) as ChatFrame);
      };
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) {
          flushLine(buf.slice(0, nl));
          buf = buf.slice(nl + 1);
        }
      }
      flushLine(buf);
    },
  },
  /**
   * tenant.signing.* — R3b e-signing via Scrive (per ADR-0024).
   *
   * send_for_signing returns the contract's new status (`sent`).
   * Pre-flight failures throw oRPC `BAD_REQUEST` with a structured
   * `data` payload the editor surfaces:
   *   - `no_signing_parties`
   *   - `missing_party_emails` + `missing: [{party_id, role, ordinal, name}]`
   *
   * cancel and resend_invitation operate on contracts in
   * sent/viewed/awaiting_signature.
   */
  signing: {
    /**
     * Non-mutating preflight (ADR-0032). Returns ok:true when the
     * contract is ready to send; otherwise a structured reason. The
     * UI uses this to render the right state up front instead of
     * waiting for a `send_for_signing` rejection on click.
     *
     * `scrive_account_not_provisioned` is the new one — the tenant's
     * Scrive customer account hasn't been provisioned yet (the
     * owner-admin needs to paste the PAC in the admin portal).
     */
    readiness: (input: { contract_id: string }) =>
      rpc<typeof input, SigningReadiness>('signing/readiness', input),
    /**
     * Mark an `export_only` contract as sent (no signing — the user
     * exports the PDF and sends it out-of-band). Transitions pending → sent.
     */
    mark_exported: (input: { contract_id: string }) =>
      rpc<typeof input, { contract_id: string; status: 'sent' }>(
        'signing/mark_exported',
        input,
      ),
    send_for_signing: (input: {
      contract_id: string;
      include_ort_datum?: boolean;
      /** Signing deadline in days (Scrive days_to_sign). Default 30. */
      days_to_sign?: number;
      /** Auto-reminder cadence in days; null disables. Default 7. */
      days_to_remind?: number | null;
      /** ADR-0100: sequential invites signers one at a time. Default parallel. */
      signing_order?: 'parallel' | 'sequential';
    }) =>
      rpc<
        typeof input,
        { contract_id: string; scrive_document_id: string; status: 'sent' }
      >('signing/send_for_signing', input),
    /** Draft-time signing plan (ADR-0100). */
    get_draft: (input: { contract_id: string }) =>
      rpc<typeof input, SigningDraft>('signing/get_draft', input),
    set_draft: (input: {
      contract_id: string;
      options: SigningDraftOptions;
      /** `null` = automatic. */
      signers: SigningDraftSigner[] | null;
    }) => rpc<typeof input, { ok: true }>('signing/set_draft', input),
    resend_invitation: (input: { contract_id: string }) =>
      rpc<typeof input, { ok: true }>('signing/resend_invitation', input),
    /** Extend an in-flight document's deadline by `days`. */
    prolong: (input: { contract_id: string; days: number }) =>
      rpc<typeof input, { ok: true; due_at: string }>('signing/prolong', input),
    /** Send a Scrive reminder to all not-yet-signed signatories now. */
    send_reminder: (input: { contract_id: string }) =>
      rpc<typeof input, { ok: true; reminder_sent_at: string }>(
        'signing/send_reminder',
        input,
      ),
    cancel: (input: { contract_id: string }) =>
      rpc<typeof input, { ok: true; status: 'cancelled' }>(
        'signing/cancel',
        input,
      ),
    update_signer_contact: (
      input: {
        contract_id: string;
        party_id: string;
        email?: string | null;
        phone?: string | null;
      },
    ) =>
      rpc<typeof input, { ok: true }>('signing/update_signer_contact', input),
    /**
     * Load the signer roster for the signing-step editor (per ADR-0044):
     * signing parties (with slot labels) + the current roster (explicit
     * rows, or the synthesized one-signer-per-party default).
     */
    get_signers: (input: { contract_id: string }) =>
      rpc<typeof input, GetSignersResponse>('signing/get_signers', input),
    /**
     * Replace the signer roster (per ADR-0044). Editable only while the
     * contract is draft/pending/cancelled. Emails/personnummer are not
     * required here — that's validated at send — so a partial roster can
     * be saved mid-edit.
     */
    set_signers: (input: {
      contract_id: string;
      signers: Array<Omit<SignerRosterEntry, 'id'>>;
    }) =>
      rpc<typeof input, { ok: true; count: number }>(
        'signing/set_signers',
        input,
      ),
    /**
     * Document-flow activity timeline for the contract. Used by the
     * SigningPanel Activity section to render the full Scrive lifecycle:
     * send → view → sign per party → completion → keyless seal.
     */
    activity: (input: { contract_id: string }) =>
      rpc<typeof input, ScriveActivityResponse>('signing/activity', input),
    /**
     * Reconciling poll: calls Scrive `getDocument` server-side, applies
     * any state changes to our DB, returns the fresh contract + parties.
     * Replaces the old `contracts.get` + `contract_parties.list`
     * polling pair while a contract is in sent/viewed/awaiting_signature.
     */
    poll_state: (input: { contract_id: string }) =>
      rpc<typeof input, ScrivePollStateResponse>('signing/poll_state', input),

    // ── Manual (non-esign) signing workflow ───────────────────────────
    /**
     * Prepare a contract for manual signing: render + freeze the final PDF
     * and flip to `awaiting_manual_signature`. Same in-flight monitoring as
     * Scrive unless `monitoring_disabled`.
     */
    prepare_manual: (input: {
      contract_id: string;
      monitoring_disabled?: boolean;
      days_to_sign?: number;
      include_ort_datum?: boolean;
    }) =>
      rpc<typeof input, { contract_id: string; status: 'awaiting_manual_signature' }>(
        'signing/prepare_manual',
        input,
      ),
    /** Mint a presigned PUT (or dev URL) for one signed document upload. */
    signed_upload_url: (input: {
      contract_id: string;
      filename: string;
      mime: string;
      bytes: number;
    }) =>
      rpc<
        typeof input,
        { document_id: string; upload_url: string; upload_key: string; expires_in: number }
      >('signing/signed_upload_url', input),
    /** Register uploaded signed document(s) and complete signing → `signed`. */
    complete_manual: (input: {
      contract_id: string;
      documents: Array<{
        upload_key: string;
        filename: string;
        mime: string;
        bytes: number;
        label?: string | null;
      }>;
    }) =>
      rpc<typeof input, { ok: true; status: 'signed' }>('signing/complete_manual', input),
    /** Cancel a manual attempt (no Scrive call). */
    cancel_manual: (input: { contract_id: string }) =>
      rpc<typeof input, { ok: true; status: 'cancelled' }>('signing/cancel_manual', input),
    /** Leave signing (in-flight Scrive/manual, cancelled or expired) and reopen as draft. */
    return_to_draft: (input: { contract_id: string }) =>
      rpc<typeof input, { ok: true; status: 'draft' }>('signing/return_to_draft', input),
    /** Read the manual-signing state + uploaded signed documents. */
    manual_state: (input: { contract_id: string }) =>
      rpc<typeof input, ManualStateResponse>('signing/manual_state', input),
  },
  users: {
    list: () => rpc<Record<string, never>, { users: UserRow[] }>('users/list'),
    list_invitations: () =>
      rpc<Record<string, never>, { invitations: PendingInvitation[] }>(
        'users/list_invitations',
      ),
    invite: (
      input: {
        email: string;
        name: string;
        group_ids?: string[];
        tenant_role?: 'org_admin' | 'normal';
        group_role?: 'admin' | 'normal';
      },
    ) =>
      rpc<typeof input, { invitation: { id: string; email_address: string; status: string } }>(
        'users/invite',
        input,
      ),
    cancel_invitation: (input: { invitation_id: string }) =>
      rpc<typeof input, { ok: true }>('users/cancel_invitation', input),
  },
};
